<!-- src/views/admin/AdminPersonnelDashboardView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24 text-left">

        <!-- 1. HEADER & INTEGRATED COMMAND FILTERS (PIXEL-PERFECT MINT ANCHOR) -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-12 animate-fadeIn relative z-30 border-x border-t border-slate-800">
          <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
            <UserGroupIcon class="h-32 w-32 text-white" />
          </div>
          
          <div class="relative z-10">
            <div class="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-8 mb-10 text-left">
              <div>
                <!-- 🛡️ ALIGNED HEADING SIZE: Matched to text-3xl -->
                <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none text-left">Personnel Command Centre</h1>
                <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4 flex items-center gap-2 text-left">
                  <span class="w-2 h-2 bg-[#20FCF1] rounded-full animate-pulse"></span>
                  Workforce Intelligence • Governance & Compliance Registry
                </p>
              </div>
              
              <div class="flex flex-wrap items-center gap-3 no-print">
                <!-- 🛡️ ALIGNED STYLING & SIZING: Swapped action priority and matched padding to py-3.5 -->
                <router-link v-slot="{ href, navigate }" v-if="canManageOptions" to="/admin/roles" custom>
                  <a :href="href" @click="navigate" class="px-6 py-3.5 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2 no-underline cursor-pointer">
                    <ShieldCheckIcon class="h-4 w-4" /> <span>Manage Roles</span>
                  </a>
                </router-link>

                <router-link v-slot="{ href, navigate }" v-if="canManageOptions" to="/admin/users/new" custom>
                  <a :href="href" @click="navigate" class="px-6 py-3.5 bg-white/10 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 shadow-lg no-underline cursor-pointer">
                    <PlusIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Enrol Member</span>
                  </a>
                </router-link>

                <router-link v-slot="{ href, navigate }" to="/client-admin" custom>
                  <a :href="href" @click="navigate" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 no-underline cursor-pointer">
                    <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
                  </a>
                </router-link>
              </div>
            </div>

            <!-- INTEGRATED FILTER ROW (Aligned to 3 columns + Export Button Inline) -->
            <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-6 bg-white/5 p-6 rounded-[32px] border border-white/10 text-left">
              <div class="flex flex-wrap gap-4 items-center flex-grow">
                
                <!-- Field 1: Search -->
                <div class="flex flex-col gap-1.5 min-w-[240px] flex-grow sm:flex-none text-left">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2 text-left">Search Registry</span>
                  <input v-model="searchTerm" type="text" placeholder="Identity Search..." class="filter-input-dark text-left" />
                </div>
                
                <!-- Field 2: Status (Premium Multi-Select Popover) -->
                <div class="relative flex flex-col gap-1.5 min-w-[240px] flex-grow sm:flex-none text-left">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2 text-left">Protocol Status</span>
                  <div class="relative">
                    <button 
                      type="button" 
                      @click="toggleStatusDropdown" 
                      class="filter-select-dark text-left flex justify-between items-center w-full px-5 py-3.5 cursor-pointer"
                    >
                      <span>{{ statusButtonLabel }}</span>
                    </button>
                    <div v-if="isStatusDropdownOpen" class="absolute z-50 mt-2 w-full bg-[#1E293B] border border-white/10 rounded-2xl p-4 shadow-2xl space-y-2 max-h-60 overflow-y-auto custom-scrollbar text-left animate-none">
                      <div 
                        v-for="opt in [
                          { value: 'all', text: 'All Statuses' },
                          { value: 'active', text: 'Active Members' },
                          { value: 'pending_onboarding', text: 'Pending Onboarding' },
                          { value: 'pending review', text: 'Pending Review' },
                          { value: 'requires action', text: 'Requires Action' },
                          { value: 'suspended', text: 'Suspended' },
                          { value: 'inactive', text: 'Inactive' }
                        ]" 
                        :key="opt.value" 
                        @click="handleStatusOptionClick(opt.value)"
                        class="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 cursor-pointer text-xs font-bold text-white uppercase tracking-tight"
                      >
                        <input 
                          type="checkbox" 
                          :checked="selectedStatusFilters.includes(opt.value)" 
                          class="h-4 w-4 rounded border-slate-300 text-[#20FCF1] focus:ring-[#20FCF1]"
                          readonly
                        />
                        <span>{{ opt.text }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Field 3: Workforce Allocation (Premium Multi-Select Popover) -->
                <div class="relative flex flex-col gap-1.5 min-w-[240px] flex-grow sm:flex-none text-left">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2 text-left">Workforce Allocation</span>
                  <div class="relative">
                    <button 
                      type="button" 
                      @click="toggleDeptDropdown" 
                      class="filter-select-dark text-left flex justify-between items-center w-full px-5 py-3.5 cursor-pointer"
                    >
                      <span>{{ deptButtonLabel }}</span>
                    </button>
                    <div v-if="isDeptDropdownOpen" class="absolute z-50 mt-2 w-full bg-[#1E293B] border border-white/10 rounded-2xl p-4 shadow-2xl space-y-2 max-h-60 overflow-y-auto custom-scrollbar text-left animate-none">
                      <div 
                        @click="handleDeptOptionClick('all')"
                        class="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 cursor-pointer text-xs font-bold text-white uppercase tracking-tight"
                      >
                        <input 
                          type="checkbox" 
                          :checked="selectedDeptFilters.includes('all')" 
                          class="h-4 w-4 rounded border-slate-300 text-[#20FCF1] focus:ring-[#20FCF1]"
                          readonly
                        />
                        <span>All Allocations</span>
                      </div>
                      <div 
                        @click="handleDeptOptionClick('unallocated')"
                        class="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 cursor-pointer text-xs font-bold text-white uppercase tracking-tight"
                      >
                        <input 
                          type="checkbox" 
                          :checked="selectedDeptFilters.includes('unallocated')" 
                          class="h-4 w-4 rounded border-slate-300 text-[#20FCF1] focus:ring-[#20FCF1]"
                          readonly
                        />
                        <span>Unallocated / No Department</span>
                      </div>
                      <div 
                        @click="handleDeptOptionClick('subcontractor_entity')"
                        class="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 cursor-pointer text-xs font-bold text-white uppercase tracking-tight"
                      >
                        <input 
                          type="checkbox" 
                          :checked="selectedDeptFilters.includes('subcontractor_entity')" 
                          class="h-4 w-4 rounded border-slate-300 text-[#20FCF1] focus:ring-[#20FCF1]"
                          readonly
                        />
                        <span>Sub-Contractor (Entities)</span>
                      </div>
                      <div 
                        v-for="dept in sortedDepartments" 
                        :key="dept.value" 
                        @click="handleDeptOptionClick(dept.value)"
                        class="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 cursor-pointer text-xs font-bold text-white uppercase tracking-tight"
                      >
                        <input 
                          type="checkbox" 
                          :checked="selectedDeptFilters.includes(dept.value)" 
                          class="h-4 w-4 rounded border-slate-300 text-[#20FCF1] focus:ring-[#20FCF1]"
                          readonly
                        />
                        <span>{{ dept.text }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Export button inline -->
              <button 
                @click="exportToExcel"
                :disabled="exporting || filteredUsers.length === 0"
                class="px-8 py-4 bg-white/10 border border-white/20 text-white rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] hover:bg-white/20 transition-all flex items-center gap-3 disabled:opacity-50 shadow-xl cursor-pointer"
              >
                <template v-if="exporting">
                  <div class="animate-spin h-3 w-3 border-2 border-[#0A1630] border-t-transparent rounded-full"></div>
                  <span>Exporting...</span>
                </template>
                <template v-else>
                  <TableCellsIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Export Current List</span>
                </template>
              </button>
            </div>
          </div>
        </div>

        <!-- 2. SENTINEL INTELLIGENCE & INTEGRATED METRICS HUB -->
        <div class="mb-12 animate-fadeIn relative z-10 text-left">
          <div class="bg-white border-2 border-slate-100 rounded-[40px] shadow-2xl overflow-hidden relative text-left">
            <div class="bg-[#0A1630] px-10 py-6 border-b-4 border-[#20FCF1] flex flex-col md:flex-row justify-between items-center gap-6">
               <div class="flex items-center gap-4 text-left">
                  <div class="bg-[#20FCF1]/10 p-3 rounded-2xl text-left">
                    <SparklesIcon class="h-6 w-6 text-[#20FCF1]" />
                  </div>
                  <div class="text-left">
                    <h2 class="text-xl font-black text-white uppercase tracking-tight leading-none text-left">Sentinel Intelligence</h2>
                    <p class="text-[8px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mt-2 text-left">Active Workforce Synthesis • Deterministic Audit Logic</p>
                  </div>
               </div>
               
               <button 
                 @click="triggerSynthesis" 
                 :disabled="isAiLoading"
                 class="px-6 py-3 bg-[#20FCF1] text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg"
               >
                 <span v-if="isAiLoading" class="animate-spin h-3 w-3 border-2 border-[#0A1630] border-t-transparent rounded-full"></span>
                 <SignalIcon v-else class="h-4 w-4" />
                 <span>{{ isAiLoading ? 'Synthesising...' : 'Synchronise Intelligence' }}</span>
               </button>
            </div>

            <div class="p-8 md:p-12 text-left">
               <!-- 📊 INTEGRATED METRICS ROW inside Sentinel Intelligence Card -->
               <div class="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 border-b border-slate-100 pb-8 text-left">
                 <div v-for="stat in personnelStats" :key="stat.label" class="flex items-center gap-4 text-left">
                   <div class="relative w-12 h-12 flex items-center justify-center text-center">
                     <svg class="absolute inset-0 w-full h-full" viewBox="0 0 24 24" fill="none">
                         <path d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" :fill="stat.color" fill-opacity="0.1" :stroke="stat.color" stroke-width="1.5"/>
                     </svg>
                     <component :is="stat.icon" class="w-5 h-5" :style="{ color: stat.color }" />
                   </div>
                   <div class="text-left">
                     <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 text-left">{{ stat.label }}</p>
                     <h4 class="text-xl font-black text-[#0A1630] leading-none text-left">{{ stat.value }}</h4>
                   </div>
                 </div>
               </div>

               <!-- Initial State -->
               <div v-if="!aiReport && !isAiLoading" class="flex flex-col items-center justify-center py-12 text-center">
                  <div class="bg-slate-50 p-6 rounded-full mb-6">
                    <CpuChipIcon class="h-10 w-10 text-slate-200" />
                  </div>
                  <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">Awaiting Initialisation</h3>
                  <p class="text-xs text-slate-400 font-bold mt-2 max-w-sm">Trigger a synchronisation to audit current workforce status, onboarding compliance, and operational bottlenecks.</p>
               </div>

               <!-- 🛡️ LOCAL SYNTHESIS CONTENT -->
               <div v-else-if="aiReport" class="grid grid-cols-1 lg:grid-cols-3 gap-10">
                  <div class="lg:col-span-2 space-y-8">
                     <div class="border-l-4 border-[#20FCF1] pl-6">
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-3">Executive Summary</h4>
                        <p class="text-sm font-medium text-slate-600 leading-relaxed italic">
                          "{{ aiReport.summary }}"
                        </p>
                     </div>

                     <div>
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Priority Interventions</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div v-for="(action, idx) in aiReport.actions" :key="idx" class="bg-slate-50 p-5 rounded-3xl border border-slate-100 flex flex-col justify-between">
                              <div>
                                <span class="text-[8px] font-black text-[#20FCF1] uppercase tracking-widest block mb-2">{{ action.type }}</span>
                                <p class="text-xs font-black text-[#0A1630] leading-tight mb-3 uppercase tracking-tight">{{ action.instruction }}</p>
                              </div>
                              <p class="text-[10px] text-slate-500 font-medium leading-relaxed">{{ action.reason }}</p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div class="bg-[#0A1630]/5 p-8 rounded-[32px] border border-slate-100 h-fit">
                    <h4 class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.3em] mb-6 border-b border-[#0A1630]/10 pb-4">Onboarding & Risk Trends</h4>
                    <div class="space-y-6">
                       <div v-for="(trend, idx) in aiReport.trends" :key="idx" class="flex items-start gap-4">
                          <div :class="trend.isCritical ? 'bg-rose-500' : 'bg-[#20FCF1]'" class="h-2 w-2 rounded-full shrink-0 mt-1.5 shadow-sm"></div>
                          <div>
                            <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-tight">{{ trend.label }}</p>
                            <p class="text-[11px] text-slate-500 font-medium mt-1 leading-tight">{{ trend.value }}</p>
                          </div>
                       </div>
                    </div>
                    <div class="mt-8 pt-6 border-t border-[#0A1630]/10 text-[9px] font-black text-slate-400 uppercase">
                      Analysis Depth: Complete Client Registry
                    </div>
                  </div>
               </div>

               <!-- Loading State -->
               <div v-if="isAiLoading" class="flex flex-col items-center justify-center py-12">
                  <div class="animate-spin h-12 w-12 border-4 border-[#20FCF1] border-t-transparent rounded-full mb-4"></div>
                  <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">Aggregating Profile Data & Synthesising Findings...</p>
               </div>
            </div>
          </div>
        </div>

        <!-- 4. OPERATIONAL REGISTRY TABLE -->
        <div v-if="isLoading || isLoadingRoles" class="text-center py-32 bg-white rounded-[40px] border border-slate-100 shadow-inner text-center">
          <div class="animate-spin h-12 w-12 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-6 text-center"></div>
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Initialising Personnel Data...</p>
        </div>

        <div v-else-if="filteredUsers.length === 0" class="text-center py-32 bg-white rounded-[40px] border border-2 border-dashed border-slate-200 animate-fadeIn text-center">
           <MagnifyingGlassIcon class="h-16 w-16 text-slate-200 mx-auto mb-6 opacity-30 text-center" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight text-center">No Matching Identity Identified</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2 text-center">Adjust search or filter protocols.</p>
        </div>

        <div v-else class="bg-white shadow-2xl rounded-[40px] overflow-hidden border border-slate-200 animate-fadeIn text-left">
          <div class="overflow-x-auto text-left">
            <table class="min-w-full text-left">
              <thead class="bg-[#0A1630] text-left">
                <tr>
                  <!-- Click-to-Sort Headers [1.1.4] -->
                  <th scope="col" @click="handleSort('identity')" class="pl-8 pr-4 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest cursor-pointer select-none hover:text-white transition-colors">
                    <div class="flex items-center gap-1.5">
                      <span>Member Identity</span>
                      <span v-if="sortBy === 'identity'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                    </div>
                  </th>
                  <th scope="col" @click="handleSort('registered')" class="px-4 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest cursor-pointer select-none hover:text-white transition-colors">
                    <div class="flex items-center gap-1.5">
                      <span>Registered</span>
                      <span v-if="sortBy === 'registered'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                    </div>
                  </th>
                  <th scope="col" @click="handleSort('rating')" class="px-4 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest cursor-pointer select-none hover:text-white transition-colors">
                    <div class="flex items-center gap-1.5">
                      <span>Performance Index</span>
                      <span v-if="sortBy === 'rating'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                    </div>
                  </th>
                  <th scope="col" @click="handleSort('status')" class="px-4 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest cursor-pointer select-none hover:text-white transition-colors">
                    <div class="flex items-center gap-1.5">
                      <span>Operational Status</span>
                      <span v-if="sortBy === 'status'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                    </div>
                  </th>
                  <th scope="col" class="pr-8 pl-4 py-6 text-right text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Command</th>
                </tr>
              </thead>
              <tbody class="text-left divide-y divide-slate-100">
                <template v-for="group in groupedUsers" :key="group.roleName">
                  <tr class="text-left">
                    <td colspan="5" class="px-8 py-5 bg-slate-50/80 border-y border-slate-100 text-left">
                      <div class="flex items-center gap-3 text-left">
                        <div class="w-1.5 h-6 rounded-full bg-[#20FCF1] shadow-[0_0_10px_rgba(56,227,176,0.5)]"></div>
                        <h3 class="text-[11px] font-black text-[#0A1630] uppercase tracking-[0.3em] text-left">
                          {{ getRoleDetails(group.roleName).pluralName }} ({{ group.users.length }})
                        </h3>
                      </div>
                    </td>
                  </tr>

                  <template v-for="user in group.users" :key="user.uid">
                    <tr class="hover:bg-slate-50/50 transition-colors group text-left">
                      <!-- Left Column: Identity Info (Clickable row trigger) [1.1.4] -->
                      <td @click="toggleRow(user.uid)" class="pl-8 pr-4 py-7 whitespace-nowrap text-left cursor-pointer">
                        <div class="flex items-center text-left">
                          <div class="flex-shrink-0 h-16 w-16 text-left">
                            <img v-if="user.photoUrl" class="h-full w-full rounded-2xl object-cover border-4 border-slate-100 shadow-lg group-hover:scale-105 transition-transform" :src="user.photoUrl" alt="Photo">
                            <div v-else class="h-full w-full rounded-2xl bg-slate-100 flex items-center justify-center font-black text-slate-300 text-sm border border-slate-200">
                              {{ (user.firstName ? user.firstName[0] : '') + (user.lastName ? user.lastName[0] : '') }}
                            </div>
                          </div>
                          <div class="ml-4 text-left">
                            <div class="text-[15px] font-black text-[#0A1630] uppercase tracking-tight leading-none mb-2 text-left">{{ user.firstName }} {{ user.lastName }}</div>
                            <div class="flex flex-wrap items-center gap-2 text-left">
                               <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest text-left">{{ (user.jobTitle || (user.employmentType === 'sub-contractor' ? 'Supply Chain Partner' : 'Standard Personnel')).replace(/_/g, ' ') }}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      <!-- Date Registered (Turns red if Pending after 7 days) [1.1.4] -->
                      <td class="px-4 py-7 whitespace-nowrap text-left">
                        <div 
                          :class="isOverduePending(user) ? 'text-red-500 font-bold' : 'text-[#0A1630] font-black'"
                          class="text-[10px] tracking-widest uppercase text-left"
                        >
                          {{ formatDate(user.createdAt) }}
                        </div>
                        <div 
                          :class="isOverduePending(user) ? 'text-red-500' : 'text-slate-300'"
                          class="text-[8px] font-bold mt-1.5 uppercase tracking-widest text-left"
                        >
                          {{ isOverduePending(user) ? 'EXPIRED • 7-DAY LIMIT' : 'Protocol Initiated' }}
                        </div>
                      </td>

                      <td class="px-4 py-7 whitespace-nowrap text-left">
                        <!-- 🛡️ FIXED SYNTAX ERROR (Issue #1 resolved): Removed v-slot:default completely from list wrapper -->
                        <div v-if="getUserAverageRating(user)" class="flex items-center gap-2 text-left">
                          <StarIcon class="h-4 w-4 text-amber-500 fill-amber-400" />
                          <span class="text-sm font-black text-[#0A1630]">{{ getUserAverageRating(user).toFixed(1) }}</span>
                          <span class="text-[9px] text-slate-300 font-bold uppercase tracking-widest ml-1">Rating</span>
                        </div>
                        <span v-else class="text-[9px] font-black uppercase text-slate-200 tracking-widest italic text-left">No Index</span>
                      </td>

                      <td class="px-4 py-7 whitespace-nowrap text-left">
                        <div class="flex flex-col gap-2 text-left">
                          <span :class="statusClass(user.status)" class="px-4 py-1.5 inline-flex text-[9px] font-black rounded-full border uppercase tracking-widest shadow-sm w-fit text-left">
                            {{ (user.status || 'pending').replace('_', ' ') }}
                          </span>
                          <div class="flex flex-wrap gap-2 text-left">
                            <span v-if="user.isDepartmentManager" class="bg-blue-50 text-blue-600 px-2 py-0.5 rounded text-[8px] font-black uppercase border border-blue-100 text-left">Manager</span>
                            <span v-if="user.isDepartmentDirector" class="bg-purple-50 text-purple-600 px-2 py-0.5 rounded text-[8px] font-black uppercase border border-purple-100 text-left">Director</span>
                          </div>
                        </div>
                      </td>

                      <!-- 🛡️ DIRECT SITE REVIEWS COMMAND BUTTON: Links up the reviews list route securely by path (Issue #2 resolved) -->
                      <td class="pr-8 pl-4 py-7 whitespace-nowrap text-right flex items-center justify-end gap-3">
                        <router-link 
                          v-slot="{ href, navigate }"
                          :to="{ name: 'UserReviewsList', params: { userId: user.uid } }" 
                          custom
                        >
                          <a :href="href" @click="navigate" class="bg-white border-2 border-slate-200 text-[#0A1630] hover:bg-[#20FCF1] hover:text-[#0A1630] px-5 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:border-transparent transition-all shadow-sm inline-flex items-center gap-2 no-underline cursor-pointer">
                            <StarIcon class="h-4 w-4 text-[#FD5E00]" /> <span>Reviews</span>
                          </a>
                        </router-link>

                        <router-link 
                          v-slot="{ href, navigate }"
                          :to="{ name: 'AdminUserProfile', params: { userId: user.uid } }" 
                          custom
                        >
                          <a :href="href" @click="navigate" class="bg-[#0A1630] text-white px-5 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-md inline-flex items-center gap-3 group-hover:scale-105 no-underline cursor-pointer">
                            <IdentificationIcon class="h-4 w-4" /> <span>Inspect Profile</span>
                          </a>
                        </router-link>
                      </td>
                    </tr>

                    <!-- Expandable Admin Comments & Performance Reviews Tray [1.1.4] -->
                    <tr v-if="expandedUserId === user.uid" class="bg-slate-50 text-left">
                      <td colspan="5" class="px-8 py-6 border-b border-slate-100 text-left">
                        <!-- 🛡️ DUAL-COLUMN LAYOUT: Aligns Admin Notes and Site Performance Reviews Timeline -->
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-10 animate-fadeIn text-left">
                          
                          <!-- Left Column: Admin Notes -->
                          <div class="space-y-4 text-left">
                            <div class="flex items-center gap-3">
                              <DocumentTextIcon class="h-4 w-4 text-[#20FCF1]" />
                              <h4 class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Administrative Notes for {{ user.firstName }} {{ user.lastName }}</h4>
                            </div>
                            <textarea 
                              v-model="user.adminNotes" 
                              rows="5" 
                              placeholder="Enter administrative notes, status reasonings (e.g. why the user is set to 'Requires Action'), or audit remarks..."
                              class="w-full bg-white border border-slate-200 rounded-xl p-5 text-xs font-medium text-slate-800 focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all shadow-inner"
                            ></textarea>
                            <div class="flex justify-end">
                              <button 
                                @click="saveAdminNotes(user)" 
                                :disabled="savingNotesId === user.uid"
                                class="bg-[#0A1630] text-[#20FCF1] px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all border border-[#20FCF1] flex items-center gap-2 cursor-pointer"
                              >
                                <div v-if="savingNotesId === user.uid" class="animate-spin h-3.5 w-3.5 border-2 border-[#20FCF1] border-t-transparent rounded-full"></div>
                                <span>{{ savingNotesId === user.uid ? 'Syncing...' : 'Save Notes' }}</span>
                              </button>
                            </div>
                          </div>

                          <!-- Right Column: Operational Reviews Timeline -->
                          <div class="space-y-4 text-left border-t lg:border-t-0 lg:border-l border-slate-200/60 pt-6 lg:pt-0 lg:pl-10">
                            <div class="flex items-center justify-between">
                              <div class="flex items-center gap-3">
                                <StarIcon class="h-4 w-4 text-[#20FCF1]" />
                                <h4 class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Site Performance Reviews Timeline</h4>
                              </div>
                              <span class="text-[8px] font-black text-slate-400 uppercase tracking-widest">
                                {{ getUserReviewsCount(user) }} Submissions
                              </span>
                            </div>

                            <!-- Review Timeline Container -->
                            <div class="space-y-3 max-h-60 overflow-y-auto custom-scrollbar pr-2">
                              <div v-if="getUserReviewsCount(user) === 0" class="flex flex-col items-center justify-center py-12 text-center bg-white border border-dashed border-slate-200 rounded-2xl">
                                <StarIcon class="h-6 w-6 text-slate-200 mb-2" />
                                <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest">No Site Evaluations Found</p>
                              </div>
                              <template v-else>
                                <!-- 🛡️ DYNAMIC CLICKABLE ROUTING LINK (Issue #3 resolved): Opens the completed form natively on-screen when clicked -->
                                <router-link 
                                  v-for="review in (reviewsByUserMap[user.uid] || [])" 
                                  :key="review.id" 
                                  :to="{ name: 'completedForm', params: { collectionName: 'user_reviews', submissionId: review.id } }"
                                  class="p-4 bg-white border border-slate-100 rounded-2xl shadow-sm space-y-2 border-l-4 border-l-[#20FCF1] hover:border-l-[#FD5E00] transition-colors block no-underline cursor-pointer"
                                >
                                  <div class="flex justify-between items-start">
                                    <div class="flex flex-col gap-1 text-left">
                                      <div class="flex items-center gap-1.5 text-left">
                                        <StarIcon class="h-3.5 w-3.5 text-amber-500 fill-amber-400" />
                                        <!-- 🛡️ DYNAMIC RATING RESOLVER: Pulls score correctly from nested formData -->
                                        <span class="text-xs font-black text-[#0A1630]">{{ (review.formData?.rating || review.rating || 0).toFixed(1) }}</span>
                                        <span class="text-[9px] text-slate-300 font-bold uppercase tracking-widest ml-1">• By: {{ review.formData?.reviewerName || review.reviewerName || 'Site Manager' }}</span>
                                      </div>
                                      <span v-if="review.formData?.siteName || review.siteName" class="text-[8px] font-black text-[#20FCF1] uppercase tracking-widest leading-none mt-1">
                                        Site: {{ review.formData?.siteName || review.siteName }}
                                      </span>
                                    </div>
                                    <span class="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                                      {{ formatReviewDate(review.formData?.submittedAt || review.submittedAt || review.date) }}
                                    </span>
                                  </div>
                                  <p class="text-[11px] font-medium text-slate-600 leading-relaxed italic pr-2 text-left">
                                    <!-- 🛡️ DYNAMIC REVIEW RESOLVER: Resolves nested evaluation comments correctly -->
                                    "{{ review.formData?.comments || review.comments || review.feedback || 'No written feedback comments recorded in this transaction.' }}"
                                  </p>
                                </router-link>
                              </template>
                            </div>
                          </div>

                        </div>
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </table>
          </div>

          <!-- Symmetrical Pagination & Qty Controls (Forced h-11 heights) -->
          <div v-if="totalPages > 1 || itemsPerPage !== 25" class="bg-slate-50 px-8 py-6 border-t border-slate-100 flex flex-col sm:flex-row justify-center items-center gap-6 no-print">
            <div class="flex items-center gap-4">
              <button 
                @click="prevPage" 
                :disabled="currentPage === 1"
                class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
              >
                &larr; Prev
              </button>
              <span class="text-xs font-black text-[#0A1630] uppercase tracking-widest">
                Page {{ currentPage }} of {{ totalPages || 1 }}
              </span>
              <button 
                @click="nextPage" 
                :disabled="currentPage === totalPages || totalPages === 0"
                class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
              >
                Next &rarr;
              </button>
            </div>
            
            <!-- Symmetrical Neon-Chevron Items Per Page Selector (Forced h-11 height) -->
            <div class="flex items-center gap-3">
              <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-widest">Display:</span>
              <select 
                v-model="itemsPerPage" 
                class="h-11 bg-[#0A1630] text-white rounded-xl px-5 text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer shadow-md appearance-none pr-8 text-center border-none"
                style="background-image: url('data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2320FCF1\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E'); background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 0.75rem;"
              >
                <option :value="25">25 Rows</option>
                <option :value="50">50 Rows</option>
                <option :value="100">100 Rows</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, watch, computed, onUnmounted } from 'vue';
import { db } from '@/firebaseConfig';
import { collection, query, where, doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUserStore } from '@/stores/userStore';
import * as XLSX from 'xlsx';
import { 
  UserGroupIcon, ArrowUturnLeftIcon, TableCellsIcon, 
  MagnifyingGlassIcon, ShieldCheckIcon, UserIcon, 
  StarIcon, PlusIcon, IdentificationIcon, DocumentTextIcon,
  SparklesIcon, CpuChipIcon, SignalIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const functions = getFunctions(userStore.auth?.app, 'europe-west2');

const users = ref([]);
const reviews = ref([]);
const isLoading = ref(true);
const exporting = ref(false);
const allRoles = ref([]);
const departmentOptions = ref([]);

// PAGINATION STATE
const currentPage = ref(1);
const itemsPerPage = ref(25);

// MULTI-SELECT DROPDOWN UI STATE
const isStatusDropdownOpen = ref(false);
const isDeptDropdownOpen = ref(false);
const selectedStatusFilters = ref(['all']);
const selectedDeptFilters = ref(['all']);

// 🛡️ SYNTHESIS STATE REGISTRY
const aiReport = ref(null);
const isAiLoading = ref(false);

// 🛡️ STICKY FILTERS: Load default states from localStorage to maintain admin context [1.1.4]
const searchTerm = ref(localStorage.getItem('sentinel_sticky_search') || '');
const selectedRoleFilter = ref(localStorage.getItem('sentinel_sticky_role') || 'all');
const selectedStatusFilter = ref(localStorage.getItem('sentinel_sticky_status') || 'all');
const selectedDepartmentFilter = ref(localStorage.getItem('sentinel_sticky_dept') || 'all');
const isLoadingRoles = ref(true);

const expandedUserId = ref(null);
const savingNotesId = ref('');

// 🛡️ ACTIVE WORKSPACE ID RESOLUTION
const currentClientId = computed(() => userStore.clientId || userStore.userProfile?.clientId);

// 🛡️ INTERACTIVE SORTING: Default matrix sets 'registered' (creation date) in descending order (latest at top) [1.1.4]
const sortBy = ref('registered');
const sortDirection = ref('desc');

// 🛡️ STICKY WATCHERS: Automatically write selections back to localStorage on change [1.1.4]
watch(searchTerm, (val) => localStorage.setItem('sentinel_sticky_search', val));
watch(selectedRoleFilter, (val) => localStorage.setItem('sentinel_sticky_role', val));
watch(selectedStatusFilter, (val) => localStorage.setItem('sentinel_sticky_status', val));
watch(selectedDepartmentFilter, (val) => localStorage.setItem('sentinel_sticky_dept', val));

/** 🛡️ PH 18: Unified Handshake Access Protocol */
const canManageOptions = computed(() => {
  // Any user with permission to access this Command Centre is authorized to manage options [1.1.4]
  return userStore.hasAdminAccess;
});

const formatDate = (timestamp) => {
  if (!timestamp) return 'System Entry';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase();
};

const formatReviewDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getDeptLabel = (val) => {
  if (!val) return 'General';
  const match = departmentOptions.value.find(o => o.value === val);
  return match ? match.text : val.replace(/_/g, ' ').toUpperCase();
};

const formatHeader = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const sortedDepartments = computed(() => {
    return [...departmentOptions.value].sort((a, b) => a.text.localeCompare(b.text));
});

// 🛡️ REAL-TIME MUTLI-TENANT REVIEWS MAPPER
const reviewsByUserMap = computed(() => {
  const map = {};
  reviews.value.forEach(r => {
    // Both 'targetUserId' and legacy 'userId' contain the recipient's UID
    const uid = r.formData?.targetUserId || r.formData?.userId || r.targetUserId || r.userId;
    if (uid) {
      if (!map[uid]) map[uid] = [];
      map[uid].push(r);
    }
  });

  // Sort chronological descending
  Object.keys(map).forEach(uid => {
    map[uid].sort((a, b) => {
      const dateA = a.formData?.submittedAt?.toDate ? a.formData.submittedAt.toDate().getTime() : new Date(a.formData?.submittedAt || a.submittedAt || 0).getTime();
      const dateB = b.formData?.submittedAt?.toDate ? b.formData.submittedAt.toDate().getTime() : new Date(b.formData?.submittedAt || b.submittedAt || 0).getTime();
      return dateB - dateA;
    });
  });

  return map;
});

// 🛡️ DYNAMIC FRONTEND RATING RESOLVERS
const getUserAverageRating = (user) => {
  const userReviews = reviewsByUserMap.value[user.uid] || [];
  if (userReviews.length === 0) return user.averageRating || null;
  const sum = userReviews.reduce((acc, r) => acc + (Number(r.formData?.rating || r.rating) || 0), 0);
  return sum / userReviews.length;
};

const getUserReviewsCount = (user) => {
  return (reviewsByUserMap.value[user.uid] || []).length;
};

const averageRatingValue = computed(() => {
    const rated = users.value.filter(u => getUserAverageRating(u));
    return rated.length ? rated.reduce((s, u) => s + (getUserAverageRating(u) || 0), 0) / rated.length : 0;
});

const personnelStats = computed(() => {
    return [
      { label: 'Total Registry', value: users.value.length, color: '#20FCF1', icon: UserGroupIcon },
      { label: 'Pending Compliance', value: users.value.filter(u => String(u.status || '').toLowerCase().includes('pending')).length, color: '#EF4444', icon: ShieldCheckIcon },
      { label: 'Average Rating', value: averageRatingValue.value.toFixed(1), color: '#FBBF24', icon: StarIcon },
      { label: 'Administrative Access', value: users.value.filter(u => {
          const r = String(u.role || '').toLowerCase().trim();
          return r === 'admin' || r === 'super-admin' || u.isDepartmentManager === true || u.isDepartmentDirector === true;
      }).length, color: '#3B82F6', icon: UserIcon }
    ];
});

const getRoleName = (u) => {
  if (u.role === 'manager' && u.permissionRoleId) {
    return allRoles.value.find(r => r.id === u.permissionRoleId)?.name || 'Manager';
  }
  if ((u.lastName || '').includes('(Entity)') || String(u.employmentType).toLowerCase() === 'sub-contractor') {
      return 'SUPPLY CHAIN ENTITY';
  }
  return String(u.role || 'Standard').toUpperCase();
};

const getRoleDetails = (roleName) => {
  const r = (roleName || '').toLowerCase();
  if (r.includes('entity')) return { pluralName: 'Supply Chain Entities', icon: UserGroupIcon };
  if (r.includes('admin')) return { pluralName: 'Administrative Oversight', icon: ShieldCheckIcon };
  if (r.includes('manager')) return { pluralName: `${roleName} Clearance Tiers`, icon: UserGroupIcon };
  return { pluralName: 'Standard Operations', icon: UserIcon };
};

const isOverduePending = (user) => {
  const status = String(user.status || '').toLowerCase().trim().replace(/_/g, ' ');
  // 🛡️ RE-ALIGNED GATING: Only trigger the 7-day limit alert strictly if the user is stuck in pending onboarding [1.1.4]
  if (status !== 'pending onboarding' || !user.createdAt) return false;
  
  const createdDate = user.createdAt.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
  const now = new Date();
  const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
  
  return (now - createdDate) > sevenDaysInMs;
};

// 🛡️ DYNAMIC ROW SELECTION (Instantly opens using pre-cached memory)
const toggleRow = (uid) => {
  expandedUserId.value = expandedUserId.value === uid ? null : uid;
};

// 🛡️ INTERACTIVE SORT HANDLER: Implements two-way toggling on consecutive clicks [1.1.4]
const handleSort = (columnKey) => {
  if (sortBy.value === columnKey) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortBy.value = columnKey;
    // Default to descending for dates and ratings, ascending for textual lists
    sortDirection.value = (columnKey === 'registered' || columnKey === 'rating') ? 'desc' : 'asc';
  }
};

const saveAdminNotes = async (user) => {
  savingNotesId.value = user.uid;
  try {
    const userDocRef = doc(db, 'userProfiles', user.uid);
    await setDoc(userDocRef, { adminNotes: user.adminNotes || '' }, { merge: true });
    alert("Identity registry notes synchronised successfully.");
  } catch (e) {
    console.error("Failed to save admin notes:", e);
    alert("Sync Failure: " + e.message);
  } finally {
    savingNotesId.value = '';
  }
};

// 🛡️ DETERMINISTIC SYNTHESIS ENGINE
const triggerSynthesis = async () => {
    isAiLoading.value = true;
    const cid = currentClientId.value;
    if (!cid) { alert("Identity context missing."); isAiLoading.value = false; return; }
    
    await new Promise(r => setTimeout(r, 1500));

    try {
        const allUsers = users.value || [];
        const total = allUsers.length;
        const pendingCount = allUsers.filter(u => String(u.status || '').toLowerCase().includes('pending')).length;
        const overdueCount = allUsers.filter(u => isOverduePending(u)).length;
        const actionRequiredCount = allUsers.filter(u => {
          const s = String(u.status || '').toLowerCase().trim();
          return s === 'suspended' || s === 'requires action';
        }).length;
        
        const depts = allUsers.map(u => u.department).filter(Boolean);
        const deptCounts = depts.reduce((acc, d) => {
          acc[d] = (acc[d] || 0) + 1;
          return acc;
        }, {});
        const topDept = Object.keys(deptCounts).sort((a, b) => deptCounts[b] - deptCounts[a])[0] || 'Unallocated';
        
        const activeCount = allUsers.filter(u => String(u.status || '').toLowerCase().trim() === 'active').length;
        const activePercent = total > 0 ? Math.round((activeCount / total) * 100) : 100;

        const report = {
            summary: `Audit of ${total} personnel registry entries indicates a workforce compliance profile of ${activePercent}%. Identified ${pendingCount} profiles in onboarding stages, with ${overdueCount} classified as overdue. Active operations are centered primarily in ${getDeptLabel(topDept)}.`,
            trends: [
                { label: 'Workforce Status', value: `${activeCount} / ${total} Active Members`, isCritical: activePercent < 80 },
                { label: 'Onboarding Friction', value: `${overdueCount} Overdue (>7 days pending)`, isCritical: overdueCount > 0 },
                { label: 'Operational Risk', value: `${actionRequiredCount} Requiring Action / Suspended`, isCritical: actionRequiredCount > 0 }
            ],
            actions: [
                { type: 'Compliance Sweep', instruction: `Audit ${overdueCount} expired profiles`, reason: 'Overdue pending onboardings exceed the standard 7-day protocol limit.' },
                { type: 'Credential Review', instruction: `Inspect ${actionRequiredCount} restricted records`, reason: 'Profiles marked with restricted flags require immediate administrative review.' }
            ],
            createdAt: new Date().toISOString()
        };

        const reportRef = doc(db, 'clients', cid, 'personnel_intelligence', 'latest');
        await setDoc(reportRef, { ...report, clientId: cid }, { merge: true });
        aiReport.value = report;

    } catch (e) {
        console.error("Synthesis Protocol Failure:", e);
        alert("Registry Handshake Failure: Insufficient authorisation to save intelligence log.");
    } finally {
        isAiLoading.value = false;
    }
};

const fetchLatestAiReport = async (cid) => {
  try {
    const docSnap = await getDoc(doc(db, 'clients', cid, 'personnel_intelligence', 'latest'));
    if (docSnap.exists()) {
      aiReport.value = docSnap.data();
    }
  } catch (err) {
    console.warn("Personnel Intelligence Registry Neutral.");
  }
};

// MULTI-SELECT HANDSHAKE HANDLERS
const toggleStatusDropdown = () => {
  isStatusDropdownOpen.value = !isStatusDropdownOpen.value;
  isDeptDropdownOpen.value = false;
};

const toggleDeptDropdown = () => {
  isDeptDropdownOpen.value = !isDeptDropdownOpen.value;
  isStatusDropdownOpen.value = false;
};

const handleStatusOptionClick = (val) => {
  if (val === 'all') {
    selectedStatusFilters.value = ['all'];
  } else {
    selectedStatusFilters.value = selectedStatusFilters.value.filter(x => x !== 'all');
    if (selectedStatusFilters.value.includes(val)) {
      selectedStatusFilters.value = selectedStatusFilters.value.filter(x => x !== val);
    } else {
      selectedStatusFilters.value.push(val);
    }
    if (selectedStatusFilters.value.length === 0) {
      selectedStatusFilters.value = ['all'];
    }
  }
};

const handleDeptOptionClick = (val) => {
  if (val === 'all') {
    selectedDeptFilters.value = ['all'];
  } else {
    selectedDeptFilters.value = selectedDeptFilters.value.filter(x => x !== 'all');
    if (selectedDeptFilters.value.includes(val)) {
      selectedDeptFilters.value = selectedDeptFilters.value.filter(x => x !== val);
    } else {
      selectedDeptFilters.value.push(val);
    }
    if (selectedDeptFilters.value.length === 0) {
      selectedDeptFilters.value = ['all'];
    }
  }
};

const statusButtonLabel = computed(() => {
  if (selectedStatusFilters.value.includes('all')) return 'All Statuses';
  if (selectedStatusFilters.value.length === 1) {
    return formatHeader(selectedStatusFilters.value[0]);
  }
  return `Statuses (${selectedStatusFilters.value.length})`;
});

const deptButtonLabel = computed(() => {
  if (selectedDeptFilters.value.includes('all')) return 'All Allocations';
  if (selectedDeptFilters.value.length === 1) {
    const val = selectedDeptFilters.value[0];
    if (val === 'unallocated') return 'Unallocated / No Department';
    if (val === 'subcontractor_entity') return 'Sub-Contractor (Entities)';
    return getDeptLabel(val);
  }
  return `Allocations (${selectedDeptFilters.value.length})`;
});

const filteredUsers = computed(() => {
  let result = users.value.filter(user => user.isPrimaryContact !== true);
  
  // Custom multi-select status filtration
  if (!selectedStatusFilters.value.includes('all')) {
    result = result.filter(user => {
      const userStatus = String(user.status || 'pending').toLowerCase().trim().replace(/_/g, ' ');
      return selectedStatusFilters.value.some(f => f.toLowerCase().trim().replace(/_/g, ' ') === userStatus);
    });
  }
  
  // Custom multi-select workforce allocation filtration
  if (!selectedDeptFilters.value.includes('all')) {
    result = result.filter(u => {
      return selectedDeptFilters.value.some(filterVal => {
        if (filterVal === 'unallocated') {
          return !u.department && (!u.departments || u.departments.length === 0);
        }
        if (filterVal === 'subcontractor_entity') {
          return (u.lastName || '').includes('(Entity)') || String(u.employmentType).toLowerCase() === 'sub-contractor';
        }
        const primary = (u.department || '').toLowerCase();
        const managed = Array.isArray(u.departments) ? u.departments.map(d => d.toLowerCase()) : [];
        const target = filterVal.toLowerCase();
        return primary === target || managed.includes(target);
      });
    });
  }

  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter(u => `${u.firstName} ${u.lastName} ${u.email} ${u.sentinelNumber || ''}`.toLowerCase().includes(term));
  }
  return result;
});

// PAGINATION MATH & CONTROL PROTOCOLS
const totalPages = computed(() => {
  return Math.ceil(filteredUsers.value.length / itemsPerPage.value);
});

const paginatedUsers = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredUsers.value.slice(start, end);
});

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};

const groupedUsers = computed(() => {
  // Groups are generated strictly from the current page's active slice to avoid excess renders
  const groups = paginatedUsers.value.reduce((acc, user) => {
    const roleName = getRoleName(user);
    if (!acc[roleName]) acc[roleName] = [];
    acc[roleName].push(user);
    return acc;
  }, {});

  // Sort users inside each group dynamically based on selected criteria
  Object.keys(groups).forEach(roleName => {
    groups[roleName].sort((a, b) => {
      let valA = '';
      let valB = '';

      if (sortBy.value === 'identity') {
        valA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase().trim();
        valB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase().trim();
      } else if (sortBy.value === 'registered') {
        valA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
        valB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
      } else if (sortBy.value === 'rating') {
        valA = getUserAverageRating(a) || 0;
        valB = getUserAverageRating(b) || 0;
      } else if (sortBy.value === 'status') {
        valA = String(a.status || 'pending').toLowerCase().trim();
        valB = String(b.status || 'pending').toLowerCase().trim();
      }

      if (valA < valB) return sortDirection.value === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection.value === 'asc' ? 1 : -1;
      return 0;
    });
  });

  return Object.keys(groups).map(roleName => ({ roleName, users: groups[roleName] }));
});

const fetchDeptOptions = async (cid) => {
  try {
    const snap = await getDoc(doc(db, 'formOptions', 'departments'));
    if (snap.exists()) {
      departmentOptions.value = snap.data()[cid] || snap.data().global || [];
    }
  } catch (err) { console.error("Dept Sync Failure:", err); }
};

let unsubscribeUsers = null;
let unsubscribeReviews = null;

const fetchData = async () => {
  isLoading.value = true;
  try {
    const cid = currentClientId.value;
    if (!cid) return;
    
    await fetchDeptOptions(cid);
    await fetchLatestAiReport(cid); 

    if (unsubscribeUsers) unsubscribeUsers();
    if (unsubscribeReviews) unsubscribeReviews();

    // 🛡️ REAL-TIME SYNCHRONISATION PROTOCOL: Swapped getDocs for a real-time onSnapshot listener
    const q = query(collection(db, 'userProfiles'), where('clientId', '==', cid));
    unsubscribeUsers = onSnapshot(q, (snap) => {
      users.value = snap.docs.map(docSnap => ({ uid: docSnap.id, ...docSnap.data() }));
    }, (err) => {
      console.error("Real-time Personnel Sync Failure:", err);
    });

    // 🛡️ REAL-TIME REVIEWS LISTENER: Syncs the workspace evaluations dynamically [1.1.4]
    const qReviews = query(collection(db, 'user_reviews'), where('clientId', '==', cid));
    unsubscribeReviews = onSnapshot(qReviews, (snap) => {
      reviews.value = snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      isLoading.value = false;
    }, (err) => {
      console.error("Real-time Reviews Sync Failure:", err);
      isLoading.value = false;
    });

  } catch (err) { 
    console.error("Sync Failure:", err); 
    isLoading.value = false; 
  }
};

const fetchRoles = async () => {
  isLoadingRoles.value = true;
  try {
    const result = await httpsCallable(functions, 'getPermissionRoles')();
    allRoles.value = [{ id: 'admin', name: 'Admin' }, { id: 'standard', name: 'Standard' }, ...(result.data || [])];
  } catch (e) { console.error(e); } finally { isLoadingRoles.value = false; }
};

const statusClass = (status) => {
  const s = String(status || 'pending').toLowerCase().trim().replace(/_/g, ' ');
  if (s === 'active') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  if (s.includes('pending')) return 'bg-amber-50 text-amber-700 border-amber-100';
  if (s === 'suspended' || s === 'requires action') return 'bg-rose-50 text-rose-700 border-rose-100';
  return 'bg-slate-50 text-slate-400 border-slate-200';
};

const exportToExcel = () => {
  exporting.value = true;
  try {
    const data = filteredUsers.value.map(u => ({
      'Identity': `${u.firstName || ''} ${u.lastName || ''}`,
      'Email Address': u.email || 'N/A',
      'Job Title': (u.jobTitle || '').replace(/_/g, ' ').toUpperCase(),
      'Registered': formatDate(u.createdAt),
      'Department(s)': (u.departments || []).map(d => getDeptLabel(d)).join(', '),
      'Average Rating': getUserAverageRating(u) ? getUserAverageRating(u).toFixed(1) : 'N/A',
      'Total Reviews': getUserReviewsCount(u),
      'Status': u.status || 'Pending'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Personnel Directory");
    XLSX.writeFile(wb, `Sentinel_Personnel_Command_Audit.xlsx`);
  } catch (err) { 
    console.warn("Export Error:", err);
    alert("Operational Failure: Export directory generation failed."); 
  } finally {
    exporting.value = false; 
  }
};

watch(() => userStore.isAuthReady, (ready) => { if (ready) { fetchData(); fetchRoles(); } }, { immediate: true });

// Filters watcher resets current page back to 1 on display or filter adjustments
watch([searchTerm, selectedStatusFilters, selectedDeptFilters, itemsPerPage], () => {
  currentPage.value = 1;
});

onUnmounted(() => {
  if (unsubscribeUsers) unsubscribeUsers();
  if (unsubscribeReviews) unsubscribeReviews();
});
</script>

<style scoped>
.filter-input-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3.5 text-sm font-bold text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all placeholder-slate-500 shadow-inner text-left; }
.filter-select-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3.5 text-[10px] font-black uppercase tracking-widest text-white focus:ring-2 focus:ring-[#20FCF1] outline-none appearance-none cursor-pointer shadow-inner text-left; background-image: url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2338E3B0\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 1rem center; background-size: 0.9rem; }
.animate-fadeIn { animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
</style>